import React, { useState } from 'react'
import FlavoursTile from './FlavoursTile'
import cn from 'classnames'
import { FlavoursStepProps, Mapper, SkuMap } from '../../lib/type'
import { useTranslation } from 'next-i18next'
import {
    BundleItem,
    BundleItemOption,
    SimpleProduct
} from '@pylot-data/fwrdschema'
import { Maybe } from '@pylot-data/fwrdschema'

import { ConvertObjToArray } from 'stores/ooka_usa_store/airglobal/pylot-custom-kit-flow/src/utils/ConvertObjToArray'
import { getFlavourAmount } from 'stores/ooka_usa_store/airglobal/pylot-custom-kit-flow/src/utils/getFlavourAmount'
import fs from './Flavours.module.scss'
import FlavourAction from 'stores/ooka_usa_store/airglobal/pylot-custom-kit-flow/src/Components/FlavoursStep/FlavourAction'

// Extending the SimpleProduct type to include ooka_product_type
export type ExtendedSimpleProduct = SimpleProduct & {
    ooka_product_type?: Maybe<string>
}

function getTabFlavours(mappedFlavours: Mapper[], currentTab: string) {
    return mappedFlavours.filter((flavour) => {
        switch (currentTab) {
            case 'all':
                return true
            case 'tobacco':
                return (
                    (flavour.product as ExtendedSimpleProduct)
                        ?.ooka_product_type === 'tobacco'
                )
            case 'cbd':
                return (
                    (flavour.product as ExtendedSimpleProduct)
                        ?.ooka_product_type === 'cbd'
                )
            case 'tea':
                return (
                    (flavour.product as ExtendedSimpleProduct)
                        ?.ooka_product_type === 'tea'
                )
        }
    })
}

function aggregateUniqueUIDsBySKU(data: Maybe<BundleItem>[]) {
    const skuMap: SkuMap = {}

    data?.forEach((item, index) => {
        item?.options?.forEach((option) => {
            const sku = String(option?.product?.sku)
            if (option?.product?.sku && sku && !skuMap?.[sku]) {
                skuMap[sku] = {
                    uids: [option?.uid],
                    count: 1,
                    product: option?.product as Maybe<ExtendedSimpleProduct>,
                    pods: [index]
                }
            }

            /**
             * Check if UID already exists to avoid duplicates
             */
            if (
                option?.product?.sku &&
                !skuMap?.[sku]?.uids?.includes(String(option?.uid))
            ) {
                skuMap[sku].uids.push(String(option?.uid))
                skuMap[sku].count++
                skuMap?.[sku]?.pods?.push(index)
            }
        })
    })
    return ConvertObjToArray(skuMap)
}

export const FlavoursStep = ({
    product,
    setStep,
    step,
    selectedOptions,
    setSelectedOptions,
    addProductHandler,
    addOns,
    isAddToCartInProgress,
    isFavourite
}: FlavoursStepProps): JSX.Element => {
    const [currentTab, setTab] = useState('all')
    const tabs = [
        { label: 'All', value: 'all' },
        { label: 'Tobacco', value: 'tobacco' },
        { label: 'Tea', value: 'tea' },
        { label: 'Cbd', value: 'cbd' }
    ]
    const [favorite, setFavorite] = useState<string | null>(null)

    const flavours = product?.items
        ?.filter((item) => item?.ooka_product_option === 'pods')
        ?.sort((a, b) => {
            if (a?.required === b?.required) {
                if (a?.sku && b?.sku) {
                    return a.sku.localeCompare(b.sku)
                }
                return 0
            }
            return a?.required ? -1 : 1
        })
    const { flavour, packAmount } = getFlavourAmount(product)
    const { t } = useTranslation(['common', 'plp'])
    const selectedPods: number[] = []
    const selectedOptionsFlavours: BundleItemOption[] = []
    flavours?.forEach((flavour, index) => {
        flavour?.options?.forEach((option) => {
            if (selectedOptions?.includes(String(option?.uid))) {
                selectedOptionsFlavours.push(option!)
                selectedPods.push(index)
            }
        })
    })
    const numberOfAvailablePods = flavours?.length ?? 1

    const mappedFlavours: Mapper[] = aggregateUniqueUIDsBySKU(flavours!)

    const numberOfSelectedFlavours = selectedOptionsFlavours?.length ?? 0

    const requiredFlavours = flavours?.filter((flavour) => flavour?.required)

    const getTitle = (
        numberOfAvailablePods: number,
        requiredFlavoursLength: number
    ) => {
        if (product?.is_flavour_kit) {
            return t('PICK A PACK')
        }
        if (
            requiredFlavoursLength === numberOfAvailablePods ||
            product?.sku === 'Catalog Kit' ||
            product?.name === 'TRAVEL BUNDLE'
        ) {
            return t(`PICK ${numberOfAvailablePods} PACKS`)
        }
        return requiredFlavoursLength <= 1
            ? t('PICK ANY NUMBER OF PACKS')
            : t(`PICK AT LEAST ${requiredFlavoursLength} PACKS`)
    }

    const getSubtitle = () => {
        return product?.sku === 'Catalog Kit' ||
            product?.name === 'TRAVEL BUNDLE'
            ? t(`Each pack comes with 2 pods`)
            : t(`Each pack comes with 2 pods`)
    }

    const availableTabs = tabs.filter((tab) => {
        const flavoursForTab = getTabFlavours(mappedFlavours, tab.value)
        return flavoursForTab.length > 0
    })

    /**
     * Combine flavours for each unique product
     */
    return (
        <div>
            <div className={fs['flavour-container']}>
                <p className={fs['pick-any']}>
                    {getTitle(numberOfAvailablePods, requiredFlavours?.length!)}
                </p>
                <p className={fs['sub-pick-any']}>{getSubtitle()}</p>
                <div className={cn(fs['tabs'])}>
                    {availableTabs.map((tab) => (
                        <span
                            tabIndex={0}
                            role="button"
                            key={tab.value}
                            className={cn(
                                fs['tab'],
                                currentTab === tab.value ? fs['active'] : ''
                            )}
                            onKeyDown={() => {
                                setTab(tab.value)
                            }}
                            onClick={() => {
                                setTab(tab.value)
                            }}
                        >
                            {tab.label}
                        </span>
                    ))}
                </div>
                <div className={cn(fs['flavour-list'])}>
                    {getTabFlavours(mappedFlavours, currentTab)?.map(
                        (option, index) => {
                            const currentFlavour = selectedOptionsFlavours?.filter(
                                (flavourData) => {
                                    return option.uids.includes(flavourData.uid)
                                }
                            )
                            return (
                                <div key={index}>
                                    <FlavoursTile
                                        option={option}
                                        product={product}
                                        className="item"
                                        selectedOptions={selectedOptions}
                                        setSelectedOptions={setSelectedOptions}
                                        selectedFlavours={
                                            selectedOptionsFlavours!
                                        }
                                        qty={currentFlavour?.length}
                                        disableAddingFlavours={
                                            numberOfSelectedFlavours >=
                                                numberOfAvailablePods ||
                                            option.product?.stock_status ===
                                                'OUT_OF_STOCK'
                                        }
                                        isOutOfStock={
                                            option.product?.stock_status ===
                                            'OUT_OF_STOCK'
                                        }
                                        numberOfAvailablePods={
                                            numberOfAvailablePods
                                        }
                                        selectedPods={selectedPods}
                                        isFavourite={isFavourite}
                                        favorite={favorite}
                                        setFavorite={setFavorite}
                                        mappedFlavours={mappedFlavours}
                                    />
                                </div>
                            )
                        }
                    )}
                </div>
            </div>
            <FlavourAction
                flavour={flavour}
                packAmount={packAmount}
                product={product}
                selectedOptions={selectedOptions}
                flavours={flavours}
                selectedOptionsFlavours={selectedOptionsFlavours}
                // @ts-expect-error
                setStep={setStep}
                step={step}
                addOns={addOns}
                setSelectedOptions={setSelectedOptions}
                addProductHandler={addProductHandler}
                isAddToCartInProgress={isAddToCartInProgress}
                isFavourite={isFavourite}
            />
        </div>
    )
}

export default FlavoursStep
